import { LBtn, LField } from "@components";
import { differenceInYears, format } from "date-fns";
import { ExamineeContactInfo, ExamineeDetails } from "../store/newExamineeSlice";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import classNames from "classnames";
import { convertToSentenceCase } from "@helpers/stringHelper";
import { Race, Ethnicity, EthnicityOptions, RaceOptions } from "@store/generalTypes";
import { isValidDate } from "@helpers/dateHelpers";
import { isEmpty } from "lodash";

const getBirthDay = (birthYear: string, birthMonth: string, birthDay: string): Date => {
    return new Date(Number(birthYear), Number(birthMonth) - 1, Number(birthDay));
};

const isNumericString = (value: string): boolean => {
    return value.trim() !== "" && !isNaN(Number(value));
};

const getEthnicity = (ethnicity: string | number): string => {
    // Map string name to numeric value
    const ethnicityValue = !isNumericString(ethnicity as string)
        ? Ethnicity[ethnicity as keyof typeof Ethnicity]
        : ethnicity;
    const ethnicityOption = EthnicityOptions.find((x) => x.value === String(ethnicityValue));
    return ethnicityOption?.name ?? "";
};

const getRace = (race: string | number): string => {
    // Map string name to numeric value
    const raceValue = !isNumericString(race as string) ? Race[race as keyof typeof Race] : race;
    const raceOption = RaceOptions.find((x) => x.value === String(raceValue));
    return raceOption?.name ?? "";
};

export const ExamineeCard = ({
    bgColor,
    contactInfo,
    examineeDetails,
    loading,
    classList,
    bigLabels,
    clinician,
    onEdit
}: ExamineeCardProps): JSX.Element => {
    const classes = classNames("is-bordered-top", "is-bordered-bottom", classList);

    const titleClasses = classNames("title", bigLabels ? "is-size-3" : "is-size-4");
    const subtitleClasses = classNames(
        "mt-3",
        "mb-5",
        "has-text-grey",
        bigLabels ? "is-size-6" : "is-size-7",
        "column"
    );

    return (
        <div className={classes} style={{ backgroundColor: bgColor }}>
            <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-content-center">
                <p className={titleClasses}>{`${contactInfo.firstName} ${contactInfo.lastName}`}</p>
                {onEdit && (
                    <LBtn
                        variation="unstyled"
                        smallSpacing
                        classes="has-text-weight-bold"
                        onClick={onEdit}
                        disabled={loading}
                    >
                        Edit
                    </LBtn>
                )}
            </div>
            <div className="columns mb-0">
                <div className={subtitleClasses}>
                    <div>{contactInfo.email}</div>
                    <div>{`(${contactInfo.phoneNumber.slice(0, 3)}) ${contactInfo.phoneNumber.slice(
                        3,
                        6
                    )}-${contactInfo.phoneNumber.slice(6)}`}</div>
                </div>
                {!!clinician && (
                    <div className={subtitleClasses}>
                        <div>Overseeing Clinician</div>
                        <p className="has-text-black has-text-weight-bold">{clinician}</p>
                    </div>
                )}
            </div>

            <div className="columns">
                {isValidDate(examineeDetails?.birthYear, examineeDetails?.birthDay, examineeDetails?.birthMonth) && (
                    <div className="column py-2">
                        <LField
                            label="Birthday"
                            value={`${format(
                                getBirthDay(
                                    examineeDetails!.birthYear!,
                                    examineeDetails!.birthMonth!,
                                    examineeDetails!.birthDay!
                                ),
                                "PP"
                            )}, (${differenceInYears(
                                new Date(),
                                getBirthDay(
                                    examineeDetails!.birthYear!,
                                    examineeDetails!.birthMonth!,
                                    examineeDetails!.birthDay!
                                )
                            )})`}
                            bigLabel={bigLabels}
                        />
                    </div>
                )}
                {loading && (
                    <div className="column py-2">
                        <Skeleton />
                    </div>
                )}
                <div className="column py-2">
                    {loading && <Skeleton />}
                    {examineeDetails?.sex && (
                        <LField label="Sex" value={convertToSentenceCase(examineeDetails.sex)} bigLabel={bigLabels} />
                    )}
                </div>
            </div>

            <div className="columns">
                <div className="column py-2 has-text-overflow-ellipsis" style={{ width: "200px" }}>
                    {loading && <Skeleton />}
                    {examineeDetails?.race && (
                        <LField label="Race" value={getRace(examineeDetails.race) ?? ""} bigLabel={bigLabels} />
                    )}
                </div>
                <div className="column py-2" style={{ width: "100px" }}>
                    {loading && <Skeleton />}
                    {examineeDetails?.ethnicity && (
                        <LField
                            label="Ethnicity"
                            value={getEthnicity(examineeDetails.ethnicity) ?? ""}
                            bigLabel={bigLabels}
                        />
                    )}
                </div>
            </div>

            <div className="columns">
                <div className="column py-2">
                    {loading && <Skeleton />}
                    {examineeDetails?.highestLevelEducation && (
                        <LField
                            label="Highest Level of Education"
                            value={examineeDetails.highestLevelEducation.toString()}
                            bigLabel={bigLabels}
                        />
                    )}
                </div>
            </div>

            <div className="columns">
                <div className="column py-2" style={{ width: "200px" }}>
                    {loading && <Skeleton />}
                    {examineeDetails?.primaryLanguage && (
                        <LField label="Primary Language" value={examineeDetails.primaryLanguage} bigLabel={bigLabels} />
                    )}
                </div>
                <div className="column py-2" style={{ width: "200px" }}>
                    {loading && <Skeleton />}
                    {!isEmpty(examineeDetails?.secondaryLanguages) && (
                        <LField
                            label="Secondary Language"
                            value={examineeDetails!.secondaryLanguages!.join(", ")}
                            maxChars={35}
                            bigLabel={bigLabels}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

interface ExamineeCardProps {
    bgColor: "#F0F7FA" | "#fff";
    contactInfo: ExamineeContactInfo;
    examineeDetails?: Partial<ExamineeDetails>;
    loading?: boolean;
    classList?: string;
    bigLabels?: boolean;
    clinician?: string;
    onEdit?: () => void;
}
